import { useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/contexts/I18nContext";
import { useTenant } from "@/contexts/TenantContext";
import { trackingClient } from "@/lib/tracking/client";

interface MultiFileUploadProps {
  onFileSelect: (position: 'front' | 'left' | 'right', file: File | null, base64: string | null) => void;
  isAnalyzing: boolean;
  images: {
    front: string | null;
    left: string | null;
    right: string | null;
  };
}

type Position = 'front' | 'left' | 'right';

export const MultiFileUpload = ({ onFileSelect, isAnalyzing, images }: MultiFileUploadProps) => {
  const { t } = useI18n();
  const { currentTenant } = useTenant();
  
  const positionLabels = {
    front: t('pdf.front_view'),
    left: t('pdf.left_side'),
    right: t('pdf.right_side')
  };
  
  const [loadingStates, setLoadingStates] = useState({
    front: false,
    left: false,
    right: false
  });
  const { toast } = useToast();

  const validateFile = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      const maxSize = 10 * 1024 * 1024; // 10MB
      const minResolution = 1080; // Minimum 1080x1080 pixels

      if (!validTypes.includes(file.type)) {
        trackingClient.track({
          event_name: 'photo_quality_failed',
          metadata: { reason: 'Invalid file format' }
        });
        toast({
          title: t('photo.validation.bad_format'),
          description: t('photo.validation.bad_format'),
          variant: "destructive",
        });
        resolve(false);
        return;
      }

      if (file.size > maxSize) {
        trackingClient.track({
          event_name: 'photo_quality_failed',
          metadata: { reason: 'File too large' }
        });
        toast({
          title: t('photo.validation.too_large'),
          description: t('photo.validation.too_large'),
          variant: "destructive",
        });
        resolve(false);
        return;
      }

      // Validate image resolution
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          if (img.width < minResolution || img.height < minResolution) {
            trackingClient.track({
              event_name: 'photo_quality_failed',
              metadata: { reason: 'Low resolution' }
            });
            toast({
              title: t('photo.validation.low_resolution'),
              description: t('photo.validation.low_resolution'),
              variant: "destructive",
            });
            resolve(false);
            return;
          }
          resolve(true);
        };
        img.onerror = () => {
          trackingClient.track({
            event_name: 'photo_quality_failed',
            metadata: { reason: 'Image load error' }
          });
          toast({
            title: t('photo.validation.bad_format'),
            description: t('photo.validation.bad_format'),
            variant: "destructive",
          });
          resolve(false);
        };
        img.src = e.target?.result as string;
      };
      reader.onerror = () => {
        toast({
          title: "File read error",
          description: "Could not read the file. Please try again.",
          variant: "destructive",
        });
        resolve(false);
      };
      reader.readAsDataURL(file);
    });
  };

  const correctImageOrientation = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas to redraw image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Check if image is wider than tall (landscape/sideways)
          const isLandscape = img.width > img.height;
          
          if (isLandscape) {
            // Rotate 90 degrees counter-clockwise to make portrait
            canvas.width = img.height;
            canvas.height = img.width;
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(-Math.PI / 2);
            ctx.drawImage(img, -img.width / 2, -img.height / 2);
          } else {
            // Keep as is
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
          }
          
          // Convert to base64
          resolve(canvas.toDataURL(file.type));
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFile = async (position: Position, file: File) => {
    setLoadingStates(prev => ({ ...prev, [position]: true }));
    
    const isValid = await validateFile(file);
    if (!isValid) {
      setLoadingStates(prev => ({ ...prev, [position]: false }));
      return;
    }
    
    try {
      const correctedBase64 = await correctImageOrientation(file);
      onFileSelect(position, file, correctedBase64);
      
      // Track successful upload
      trackingClient.track({
        event_name: 'photo_uploaded',
        metadata: { 
          position,
          fileSize: file.size,
          fileType: file.type
        }
      });
    } catch (error) {
      console.error('Error correcting image orientation:', error);
      trackingClient.track({
        event_name: 'photo_quality_failed',
        metadata: { reason: 'Image processing error' }
      });
      toast({
        title: t('photo.error_processing'),
        description: t('photo.error_orientation'),
        variant: "destructive",
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [position]: false }));
    }
  };

  const handleChange = (position: Position) => (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(position, e.target.files[0]);
    }
  };

  const clearImage = (position: Position) => {
    onFileSelect(position, null, null);
  };

  const renderUploadBox = (position: Position) => {
    const isLoading = loadingStates[position];
    const preview = images[position];

    return (
      <div key={position} className="flex-1 w-full">
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-foreground text-center tracking-wide">
            {positionLabels[position]}
          </h3>
          
          {isLoading ? (
            <div className="flex flex-col items-center justify-center border-2 border-dashed rounded-2xl border-border bg-muted/30 h-[280px] md:h-[320px]">
              <Loader2 className="w-10 h-10 text-accent animate-spin" strokeWidth={2} />
              <p className="text-muted-foreground mt-3 text-sm font-light">{t('photo.processing')}</p>
            </div>
          ) : preview ? (
            <div className="relative rounded-2xl overflow-hidden shadow-medium border border-border h-[280px] md:h-[320px] group">
              <img
                src={preview}
                alt={`${positionLabels[position]} preview`}
                className="w-full h-full object-contain bg-muted/20"
              />
              {!isAnalyzing && (
                <button
                  onClick={() => clearImage(position)}
                  className="absolute top-3 right-3 bg-white/90 hover:bg-white text-foreground rounded-full p-2.5 shadow-lg transition-all hover:scale-110 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label={`Remove ${positionLabels[position]} photo`}
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-3">
                <p className="text-white text-xs font-medium">✓ {t('photo.uploaded')}</p>
              </div>
            </div>
          ) : (
            <div className="relative">
              <input
                type="file"
                id={`file-upload-${position}`}
                className="hidden"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleChange(position)}
                disabled={isAnalyzing}
              />
              
              <label
                htmlFor={`file-upload-${position}`}
                className="cursor-pointer flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-300 border-border hover:border-accent hover:bg-accent/5 h-[280px] md:h-[320px] group min-h-[44px]"
                aria-describedby={`upload-requirements-${position}`}
              >
                <div 
                  className="rounded-full p-5 shadow-medium mb-4 transition-transform group-hover:scale-110"
                  style={{
                    background: currentTenant?.primaryColor 
                      ? `linear-gradient(135deg, ${currentTenant.primaryColor}, ${currentTenant.primaryColor}dd)`
                      : undefined,
                  }}
                >
                  <Upload className="w-8 h-8 text-white" strokeWidth={2} />
                </div>
                <p className="text-sm text-foreground font-medium mb-1">
                  {t('photo.upload_button')}
                </p>
                <p className="text-xs text-muted-foreground font-light">
                  {positionLabels[position]}
                </p>
                <p 
                  id={`upload-requirements-${position}`}
                  className="text-xs text-muted-foreground mt-3 font-light"
                >
                  {t('photo.min_requirements')}
                </p>
              </label>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="mb-6 p-4 bg-muted/30 rounded-xl border border-border">
        <h3 className="text-sm font-semibold text-foreground mb-2">{t('photo.quality_title')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-muted-foreground">
          <div className="flex items-start gap-2">
            <span style={{ color: currentTenant?.primaryColor || undefined }}>✓</span>
            <span>{t('photo.quality_clear_face')}</span>
          </div>
          <div className="flex items-start gap-2">
            <span style={{ color: currentTenant?.primaryColor || undefined }}>✓</span>
            <span>{t('photo.quality_good_lighting')}</span>
          </div>
          <div className="flex items-start gap-2">
            <span style={{ color: currentTenant?.primaryColor || undefined }}>✓</span>
            <span>{t('photo.quality_sharp')}</span>
          </div>
          <div className="flex items-start gap-2">
            <span style={{ color: currentTenant?.primaryColor || undefined }}>✓</span>
            <span>{t('photo.quality_no_accessories')}</span>
          </div>
        </div>
      </div>
      
      <div className="w-full space-y-6">
        <div className="w-full">
          {renderUploadBox('front')}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {renderUploadBox('left')}
          {renderUploadBox('right')}
        </div>
      </div>
    </div>
  );
};
